import * as THREE from 'three';

/**
 * Aliases `mouthSmileRight` / `mouthSmile_R` onto an existing authored slot
 * when an export ships a left smile with no matching right one.
 *
 * This was written for Cara (f4-xandra), and the comment here used to say her
 * export had no `mouthSmileRight` and that `mouthSmileRightold` was the
 * authored right corner to alias onto. As of Aug 15 2026 that is no longer
 * true of the shipping asset: `mouthSmileRight` was authored into
 * f4-xandra.glb by mirroring `mouthSmileLeft`, so the early return below sees
 * a live right morph and this helper stands down for her.
 *
 * Keep the asset fix ahead of this aliasing path, because the alias pairs
 * shapes from two different authoring passes. Measured on f4-xandra.glb, the
 * `*old` keys are one matched pair (17,690 / 17,688 vertices moved) and the
 * current keys are another (13,616 / 13,450). Aliasing left→`...Rightold`
 * mixes them: an 87%-amplitude left against a 94%-amplitude right, which is a
 * subtler version of the lopsided smile it was meant to cure.
 */
export function ensureMirroredSmileMorph(mesh: THREE.Mesh): boolean {
  const dict = mesh.morphTargetDictionary;
  const inf = mesh.morphTargetInfluences;
  const geom = mesh.geometry;
  const morphPos = geom.morphAttributes?.position;
  if (!dict || !inf || !morphPos || !geom.attributes.position) {
    return false;
  }
  const leftIdx = dict.mouthSmileLeft ?? dict.mouthSmile_L;
  if (leftIdx == null || !morphPos[leftIdx]) {
    return false;
  }

  const liveRight = dict.mouthSmileRight;
  const authoredRight = dict.mouthSmileRightold;
  if (liveRight != null && liveRight !== leftIdx && liveRight !== authoredRight) {
    return false;
  }

  if (authoredRight != null && authoredRight !== leftIdx && morphPos[authoredRight]) {
    dict.mouthSmileRight = authoredRight;
    dict.mouthSmile_R = authoredRight;
    console.log('🙂 enamel19 aliased mouthSmileRight → mouthSmileRightold on', mesh.name, 'idx', authoredRight);
    return true;
  }

  if (liveRight != null && liveRight !== leftIdx) {
    dict.mouthSmile_R = liveRight;
    return false;
  }

  return false;
}

export function mirrorMorphAttrAcrossX(
  base: THREE.BufferAttribute,
  src: THREE.BufferAttribute,
  asNormal = false,
): THREE.BufferAttribute {
  const n = base.count;
  const out = new Float32Array(n * src.itemSize);
  const mirrorOf = buildXMirrorIndex(base);
  for (let i = 0; i < n; i++) {
    const j = mirrorOf[i];
    const sx = src.getX(j);
    const sy = src.getY(j);
    const sz = src.itemSize > 2 ? src.getZ(j) : 0;
    out[i * src.itemSize] = -sx;
    out[i * src.itemSize + 1] = sy;
    if (src.itemSize > 2) out[i * src.itemSize + 2] = asNormal ? -sz : sz;
  }
  return new THREE.BufferAttribute(out, src.itemSize);
}

function buildXMirrorIndex(base: THREE.BufferAttribute): Int32Array {
  const n = base.count;
  let minX = Infinity;
  let maxX = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = base.getX(i);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
  }
  const width = Math.max(1e-4, maxX - minX);
  const cell = Math.min(Math.max(width * 0.04, 0.05), width * 0.12);
  const maxDistSq = (width * 0.08) * (width * 0.08);
  const hash = new Map<string, number[]>();
  const keyOf = (x: number, y: number, z: number) =>
    `${Math.round(x / cell)}_${Math.round(y / cell)}_${Math.round(z / cell)}`;
  for (let i = 0; i < n; i++) {
    const k = keyOf(base.getX(i), base.getY(i), base.getZ(i));
    const bucket = hash.get(k);
    if (bucket) bucket.push(i);
    else hash.set(k, [i]);
  }
  const mirror = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const x = base.getX(i);
    const y = base.getY(i);
    const z = base.getZ(i);
    if (Math.abs(x) < cell * 0.25) {
      mirror[i] = i;
      continue;
    }
    const ix = Math.round((-x) / cell);
    const iy = Math.round(y / cell);
    const iz = Math.round(z / cell);
    let best = i;
    let bestD = Infinity;
    for (let dx = -2; dx <= 2; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        for (let dz = -2; dz <= 2; dz++) {
          const bucket = hash.get(`${ix + dx}_${iy + dy}_${iz + dz}`);
          if (!bucket) continue;
          for (const j of bucket) {
            const ddx = -x - base.getX(j);
            const ddy = y - base.getY(j);
            const ddz = z - base.getZ(j);
            const d = ddx * ddx + ddy * ddy + ddz * ddz;
            if (d < bestD) {
              bestD = d;
              best = j;
            }
          }
        }
      }
    }
    mirror[i] = bestD <= maxDistSq ? best : i;
  }
  return mirror;
}
